import { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import Icons from '../../../../../components/Common/Icons/Icons';
import { PdfViewerTrigger } from '../../../../../context/pdfViewerDialog';

function formatDate(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  };

  return date.toLocaleDateString('en-US', options).toUpperCase();
}

function getMonthDates(): { current: string; previous: string } {
  const now = new Date();

  const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  return {
    current: formatDate(currentMonth),
    previous: formatDate(previousMonth),
  };
}

const { current: currentMonthDate, previous: previousMonthDate } = getMonthDates();

export function GuidanceViewPdfButton({
  src = '/guidance/pdfs/CheckingStatement2026.pdf',
  searchActive = false,
}: {
  src?: string;
  searchActive?: boolean;
}) {
  //const pdfSrc = searchActive ? '/guidance/otherDocs/bill_highlighted.pdf' : src;
  const pdfSrc = src;

  return (
    <PdfViewerTrigger src={pdfSrc}>
      <a
        href="#"
        aria-label="download-pdf"
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
        className="border-[1px] h-[36px] border-primary bg-white text-primary px-2 md:px-[15px] py-[5px]
          flex gap-[5px] items-center hover:bg-primary hover:text-white rounded-lg whitespace-nowrap"
      >
        <Icons.OfficeFile />
        <div className="font-bold">View PDF</div>
      </a>
    </PdfViewerTrigger>
  );
}

// export function GuidanceViewWebButton() {
//   return (
//     <PdfViewerTrigger modalTitle="Statement" src="/UnreviewedDocumentPage/December-Statement_eStatements.html">
//       <a
//         href="#"
//         onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
//         target="_blank"
//         aria-label="download-pdf"
//         className="border-[1px] h-[36px] border-primary bg-white text-primary px-2 md:px-[15px] py-[5px]
//           flex gap-[5px] items-center hover:bg-primary hover:text-white rounded-lg whitespace-nowrap"
//         rel="noreferrer"
//       >
//         <Icons.GlobeIcon />
//         <div className="font-bold">View Web</div>
//       </a>
//     </PdfViewerTrigger>
//   );
// }
export function GuidanceViewWebButton({ src }: { src: string }) {
  return (
    <PdfViewerTrigger modalTitle="Statement" src={src}>
      <a
        href="#"
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
        target="_blank"
        aria-label="download-pdf"
        className="border-[1px] h-[36px] border-primary bg-white text-primary px-2 md:px-[15px] py-[5px]
          flex gap-[5px] items-center hover:bg-primary hover:text-white rounded-lg whitespace-nowrap"
        rel="noreferrer"
      >
        <Icons.GlobeIcon />
        <div className="font-bold">View Web</div>
      </a>
    </PdfViewerTrigger>
  );
}

export interface INoticesData {
  id: string;
  account: string;
  date: string;
  noticeType: string;
  reviewed: boolean;
  searchKey?: string;
  searchActive?: boolean;
  filePath?: string;
}

export interface IDocumentsData {
  id: string;
  account: string;
  date: string;
  description: string;
  reviewed: boolean;
  tax: boolean;
  statement: boolean;
  searchKey?: string;
  filePath?: string;
}

export const noticesCol: ColumnDef<INoticesData>[] = [
  {
    header: 'Account #',
    accessorKey: 'account',
    enableSorting: true,
    size: 200,
    cell: ({ row }) => <div className="font-bold">{row.getValue('account')}</div>,
  },
  {
    header: 'Date',
    accessorKey: 'date',
    size: 200,
    enableSorting: true,
  },
  {
    header: 'Notice Type',
    accessorKey: 'noticeType',
    enableSorting: true,
  },
  {
    id: 'actions',
    accessorKey: '',
    header: 'Actions',
    size: 160,
    cell: ({ row }) => (
      <GuidanceViewPdfButton
        src={row.original.filePath || '/guidance/otherDocs/rise_financial_notice.pdf'}
        searchActive={!!row.original.searchKey}
      />
    ),
  },
];

export const noticesData: INoticesData[] = [
  {
    id: '1',
    account: '****4560',
    date: previousMonthDate,
    noticeType: 'Past due loan payment',
    reviewed: true,
    filePath: '/guidance/pdfs/pastDueLoanPayment.pdf',
  },
  {
    id: '2',
    account: '****4562',
    date: previousMonthDate,
    noticeType: 'ATM deposit adjustment notice',
    reviewed: false,
    filePath: '/guidance/pdfs/atmDepositAdjustmentNotice.pdf',
  },
  {
    id: '3',
    account: '****4561',
    date: previousMonthDate,
    noticeType: 'Delinquent account notice',
    reviewed: true,
    searchKey: 'Payroll',
    filePath: '/guidance/pdfs/delinquentAccountNotice.pdf',
  },
  {
    id: '4',
    account: '****4564',
    date: previousMonthDate,
    noticeType: 'Overdraft notice',
    reviewed: false,
    filePath: '/guidance/pdfs/overdraftNotice.pdf',
  },
];

export const documentsCol: ColumnDef<IDocumentsData>[] = [
  {
    header: 'Account #',
    accessorKey: 'account',
    enableSorting: true,
    size: 200,
    cell: ({ row }) => <div className="font-bold">{row.getValue('account')}</div>,
  },
  {
    header: 'Statement date',
    accessorKey: 'date',
    size: 200,
    enableSorting: true,
  },
  {
    header: 'Description',
    accessorKey: 'description',
    enableSorting: true,
  },
  {
    id: 'actions',
    accessorKey: '',
    header: 'Actions',
    size: 160,
    cell: ({ row }) => (
      <GuidanceViewPdfButton src={'/guidance/pdfs/RISE-1098-Compliant.pdf'} searchActive={!!row.original.searchKey} />
    ),
  },
];

export const documentsData: IDocumentsData[] = [
  {
    id: '1',
    account: '****4560',
    date: previousMonthDate,
    description: '1098',
    tax: false,
    statement: true,
    reviewed: true,
    filePath: '/guidance/pdfs/RISE-1098-Compliant.pdf',
  },
  {
    id: '2',
    account: '****4562',
    date: previousMonthDate,
    description: '1099-B',
    tax: false,
    statement: false,
    reviewed: false,
    searchKey: 'ACHbsd',
    filePath: '/guidance/pdfs/RISE-1098-Compliant.pdf',
  },
  {
    id: '3',
    account: '****4561',
    date: previousMonthDate,
    description: '1099-C',
    tax: false,
    statement: true,
    reviewed: true,
    filePath: '/guidance/pdfs/RISE-1098-Compliant.pdf',
  },
  {
    id: '4',
    account: '****4561',
    date: previousMonthDate,
    description: '1099-DIV',
    tax: false,
    statement: true,
    reviewed: true,
    searchKey: 'Payroll',
    filePath: '/guidance/pdfs/RISE-1098-Compliant.pdf',
  },
  {
    id: '4',
    account: '****4564',
    date: previousMonthDate,
    description: '1099-INT',
    tax: false,
    statement: true,
    reviewed: true,
    searchKey: 'Payroll',
    filePath: '/guidance/pdfs/RISE-1098-Compliant.pdf',
  },
];
